import { percentage } from '../constants'

// The campaign report that "Report (PDF)" produces. It's a self-contained HTML document
// that the browser prints, rather than a PDF built in JavaScript, for three reasons: it needs
// no dependency (the build here is webpack 2, which chokes on current PDF libraries), the text
// stays real, selectable text instead of a screenshot, and every script the nudges are written
// in (Hausa, Hindi, Assamese...) renders with the system's own fonts. The person picks
// "Save as PDF" in the print dialog; the page title becomes the file name.

const escapeHtml = value => String(value == null ? '' : value)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')

const fmt = n => Number(n || 0).toLocaleString()

const STYLES = `
  @page { size: A4; margin: 14mm 12mm; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, 'Segoe UI', Roboto, 'Noto Sans', 'Helvetica Neue', Arial, sans-serif; color: #101828; font-size: 11.5px; line-height: 1.45; margin: 0; }
  h1 { font-size: 20px; margin: 0 0 2px; }
  h2 { font-size: 13.5px; margin: 0 0 4px; padding-top: 4px; }
  .muted { color: #667085; }
  .sub { color: #667085; font-size: 11px; margin: 0 0 8px; }
  .head { border-bottom: 2px solid #2ab6b6; padding-bottom: 8px; margin-bottom: 14px; }
  .head-meta { color: #667085; font-size: 11px; margin-top: 3px; }
  .badge { display: inline-block; padding: 1px 8px; border-radius: 10px; background: #eef2f6; font-size: 10.5px; font-weight: 600; vertical-align: middle; margin-left: 6px; }
  section { margin-bottom: 16px; page-break-inside: avoid; }
  section.flow { page-break-inside: auto; }
  .tiles { display: flex; gap: 6px; }
  .tile { flex: 1 1 0; min-width: 0; border: 1px solid #e4e7ec; border-radius: 6px; padding: 8px 10px; }
  .tile .l { font-size: 9.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; color: #667085; }
  .tile .v { font-size: 18px; font-weight: 600; margin-top: 3px; }
  .tile .s { font-size: 10px; color: #667085; }
  table { width: 100%; border-collapse: collapse; font-size: 11px; }
  th { text-align: left; font-size: 9.5px; text-transform: uppercase; letter-spacing: .04em; color: #667085; border-bottom: 1px solid #d0d5dd; padding: 4px 6px; }
  td { border-bottom: 1px solid #eef0f3; padding: 5px 6px; vertical-align: top; }
  th.n, td.n { text-align: right; font-variant-numeric: tabular-nums; }
  tr.total td { font-weight: 700; border-top: 1px solid #d0d5dd; border-bottom: 0; }
  .bar { display: inline-block; height: 7px; background: #00a0a0; border-radius: 2px; vertical-align: middle; }
  .track { display: inline-block; width: 90px; height: 7px; background: #e6f4f4; border-radius: 2px; vertical-align: middle; margin-right: 6px; }
  .track .bar { display: block; }
  .group-title { font-weight: 600; margin: 8px 0 4px; }
  .previews { display: flex; flex-wrap: wrap; gap: 12px; }
  .preview { width: 48%; page-break-inside: avoid; }
  .preview .lang { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; color: #667085; margin-bottom: 4px; }
  .chat { background: #e5ddd5; border-radius: 8px; padding: 10px; }
  .bubble { background: #fff; border-radius: 0 8px 8px 8px; padding: 8px 10px; box-shadow: 0 1px 1px rgba(0,0,0,.12); }
  .bubble .hd { font-weight: 700; margin-bottom: 3px; }
  .bubble .media { display: block; width: 100%; max-height: 160px; object-fit: cover; border-radius: 4px; margin-bottom: 4px; }
  .bubble .bd { white-space: pre-wrap; word-wrap: break-word; }
  .bubble .ft { color: #8696a0; font-size: 10px; margin-top: 4px; }
  .wa-btn { background: #fff; border-radius: 8px; margin-top: 4px; padding: 6px; text-align: center; color: #027eb5; font-weight: 600; box-shadow: 0 1px 1px rgba(0,0,0,.12); }
  .src { color: #667085; font-size: 10px; margin-top: 4px; }
  .note { color: #667085; font-size: 10px; margin-top: 6px; }
  .warn { color: #b54708; }
  .foot { margin-top: 18px; border-top: 1px solid #e4e7ec; padding-top: 6px; color: #98a2b3; font-size: 9.5px; }
`

function previewSection (preview) {
  const groups = (preview && preview.groups) || []
  if (!groups.length) return ''
  const multiple = groups.length > 1
  const body = groups.map(group => {
    const bubbles = group.languages.map(l => {
      const buttons = (l.buttons || []).map(b => `<div class="wa-btn">${escapeHtml(b)}</div>`).join('')
      return `<div class="preview">
        <div class="lang">${escapeHtml(l.language)}</div>
        <div class="chat">
          <div class="bubble">
            ${l.headerMedia && l.headerMedia.dataUrl ? `<img class="media" src="${l.headerMedia.dataUrl}" alt="${escapeHtml(l.headerMedia.mediaName || 'Header image')}">` : ''}
            ${l.headerMedia ? `<div class="hd">${escapeHtml(`[${String(l.headerMedia.format || 'Media').toLowerCase()} header] ${l.headerMedia.mediaName || 'no file set'}`)}</div>` : ''}
            ${l.header ? `<div class="hd">${escapeHtml(l.header)}</div>` : ''}
            <div class="bd">${escapeHtml(l.body)}</div>
            ${l.footer ? `<div class="ft">${escapeHtml(l.footer)}</div>` : ''}
          </div>
          ${buttons}
        </div>
        <div class="src">${l.source === 'template' ? `Approved WhatsApp template: ${escapeHtml(l.templateName)}` : 'Message text as saved with the campaign (no approved template attached)'}</div>
      </div>`
    }).join('')
    return `${multiple ? `<div class="group-title">${escapeHtml(group.groupName || `Group ${group.groupId}`)}${group.splitPercent ? ` · ${escapeHtml(group.splitPercent)}% of traffic` : ''}</div>` : ''}<div class="previews">${bubbles}</div>`
  }).join('')
  return `<section class="flow"><h2>Nudge preview</h2><p class="sub">What the user receives, in each language.</p>${body}</section>`
}

function buttonsSection (buttons, variants) {
  const groups = (buttons && buttons.groups) || []
  if (!groups.length) return ''
  const deliveredOf = id => {
    const v = (variants || []).find(x => String(x.group_id) === String(id))
    return v ? v.delivered : 0
  }
  const tables = groups.map(group => {
    const delivered = deliveredOf(group.groupId)
    const rows = group.buttons.map(b => `<tr>
      <td>${b.position}. ${escapeHtml(b.label)}</td>
      <td>${escapeHtml(b.destinationLabel)}${b.sharedDestination ? ' <span class="warn">(same destination as an earlier button, counted there)</span>' : ''}</td>
      <td class="n">${fmt(b.clicks)}</td>
      <td class="n">${fmt(b.users)}</td>
      <td class="n">${percentage(b.clicks, group.clicks)}%</td>
      <td class="n">${percentage(b.clicks, delivered)}%</td>
    </tr>`).join('')
    return `${groups.length > 1 ? `<div class="group-title">${escapeHtml(group.groupName || `Group ${group.groupId}`)}</div>` : ''}
      <table>
        <tr><th>Button</th><th>Goes to</th><th class="n">Responses</th><th class="n">Users</th><th class="n">Share of button responses</th><th class="n">% of delivered</th></tr>
        ${rows}
        <tr class="total"><td colspan="2">All buttons</td><td class="n">${fmt(group.clicks)}</td><td class="n"></td><td class="n"></td><td class="n">${percentage(group.clicks, delivered)}%</td></tr>
      </table>
      <p class="note">${fmt(group.otherReplies)} other repl${group.otherReplies === 1 ? 'y' : 'ies'} (typed messages that did not route to a button's destination) out of ${fmt(group.totalReplies)} total.</p>`
  }).join('')
  return `<section><h2>Button responses</h2>
    <p class="sub">${fmt(buttons.totalClicks)} button response${buttons.totalClicks === 1 ? '' : 's'} from ${fmt(buttons.totalClickUsers)} user${buttons.totalClickUsers === 1 ? '' : 's'}.</p>
    ${tables}
    <p class="note">A response is counted when a reply lands in the destination a button points to. Typing that button's number lands in the same place, so it counts the same.</p>
  </section>`
}

function funnelSection (stages) {
  if (!stages || !stages.length) return ''
  const rows = stages.map(s => `<tr>
    <td>${escapeHtml(s.label)}</td>
    <td class="n">${fmt(s.value)}</td>
    <td><span class="track"><span class="bar" style="width:${Math.min(Math.max(s.percent, 0), 100)}%"></span></span>${s.percent}%</td>
  </tr>`).join('')
  return `<section><h2>Milestone progression</h2><p class="sub">Distinct users at each stage, as a share of everyone nudged.</p>
    <table><tr><th>Stage</th><th class="n">Users</th><th>Share</th></tr>${rows}</table></section>`
}

function variantsSection (variants) {
  if (!variants || !variants.length) return ''
  const multiple = variants.length > 1
  const rows = variants.map(v => `<tr>
    <td>${escapeHtml(v.group_name || `Group ${v.group_id}`)}${multiple ? ` (${escapeHtml(v.split_percent || 0)}%)` : ''}</td>
    <td>${escapeHtml(v.msg_template_name || 'Generated')}</td>
    <td class="n">${fmt(v.delivered_users)}</td>
    <td class="n">${fmt(v.delivered)}</td>
    <td class="n">${fmt(v.responded)}</td>
    <td class="n">${percentage(v.responded, v.delivered)}%</td>
    <td class="n">${fmt(v.failed)}</td>
  </tr>`).join('')
  return `<section><h2>${multiple ? 'A/B variant results' : 'Content group'}</h2>
    <table><tr><th>Group</th><th>Template</th><th class="n">Users reached</th><th class="n">Delivered</th><th class="n">Replied to</th><th class="n">Reply rate</th><th class="n">Failed</th></tr>${rows}</table>
    ${multiple ? '<p class="note">Leading means the higher observed reply rate. No significance test is run.</p>' : ''}</section>`
}

function trendSection (days, caption) {
  const rows = (days || []).filter(d => d.delivered || d.responded)
  if (!rows.length) return ''
  const body = rows.map(d => {
    const rate = percentage(d.responded, d.delivered)
    return `<tr><td>${escapeHtml(d.day)}</td><td class="n">${fmt(d.delivered)}</td><td class="n">${fmt(d.responded)}</td>
      <td><span class="track"><span class="bar" style="width:${Math.min(rate, 100)}%"></span></span>${rate}%</td></tr>`
  }).join('')
  return `<section class="flow"><h2>Reply rate by day</h2><p class="sub">${escapeHtml(caption)}</p>
    <table><tr><th>Day</th><th class="n">Delivered</th><th class="n">Replied to</th><th>Reply rate</th></tr>${body}</table></section>`
}

// data: { campaign, totals, variants, buttons, preview, funnelStages, days, windowText,
//         triggerText, statusLabel, trendCaption }
export function buildCampaignReportHtml (data) {
  const { campaign, totals } = data
  const generated = new Date().toLocaleString()
  const meta = [campaign.country_name, campaign.customer_name, data.windowText, data.triggerText].filter(Boolean).join(' · ')
  const tile = (label, value, sub) => `<div class="tile"><div class="l">${label}</div><div class="v">${value}</div><div class="s">${sub}</div></div>`

  return `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(data.title)}</title><style>${STYLES}</style></head><body>
    <div class="head">
      <h1>${escapeHtml(campaign.opportunity_name || 'Nudge campaign')}<span class="badge">${escapeHtml(data.statusLabel)}</span></h1>
      <div class="head-meta">Nudge Console campaign report · ${escapeHtml(meta)}</div>
      <div class="head-meta">Generated ${escapeHtml(generated)}</div>
    </div>

    ${previewSection(data.preview)}

    <section><h2>Headline numbers</h2>
      <div class="tiles">
        ${tile('Nudges sent', fmt(totals.attempts), `${fmt(totals.unique_users)} distinct users`)}
        ${tile('Delivered', fmt(totals.delivered), `${percentage(totals.delivered, totals.attempts)}% delivery`)}
        ${tile('Replies', fmt(totals.responded), `${percentage(totals.responded, totals.delivered)}% of delivered`)}
        ${tile('Button responses', fmt(data.buttons && data.buttons.totalClicks), data.buttons && data.buttons.groups && data.buttons.groups.length ? `${fmt(data.buttons.totalClickUsers)} users` : 'no buttons')}
        ${tile('Failed', fmt(totals.failed), `${percentage(totals.failed, totals.attempts)}% of attempts`)}
      </div>
    </section>

    ${buttonsSection(data.buttons, data.variants)}
    ${funnelSection(data.funnelStages)}
    ${variantsSection(data.variants)}
    ${trendSection(data.days, data.trendCaption)}

    <div class="foot">Reply rate is nudges replied to divided by nudges delivered. Milestone figures count distinct users; the headline numbers count messages.</div>
  </body></html>`
}

// Prints the report from a hidden frame, so there's no pop-up to block and nothing to close.
// The page's own title is swapped for the duration because browsers name the PDF after it.
export function printCampaignReport (html, title) {
  const frame = document.createElement('iframe')
  frame.setAttribute('aria-hidden', 'true')
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;'
  document.body.appendChild(frame)

  const previousTitle = document.title
  document.title = title
  let cleaned = false
  const cleanup = () => {
    if (cleaned) return
    cleaned = true
    document.title = previousTitle
    if (frame.parentNode) frame.parentNode.removeChild(frame)
  }

  const frameWindow = frame.contentWindow
  const doc = frameWindow.document
  doc.open()
  doc.write(html)
  doc.close()
  frameWindow.addEventListener('afterprint', cleanup)
  // afterprint doesn't fire everywhere, so don't leave the frame behind if it doesn't.
  setTimeout(cleanup, 120000)
  // Header images are embedded as data URLs; print once they've loaded, or they come out
  // blank on the page.
  setTimeout(() => {
    const pending = Array.prototype.slice.call(doc.images)
      .filter(img => !img.complete)
      .map(img => new Promise(resolve => { img.onload = resolve; img.onerror = resolve }))
    Promise.all(pending).then(() => {
      frameWindow.focus()
      frameWindow.print()
    })
  }, 300)
}



// WEBPACK FOOTER //
// ./src/components/nudge-console/analytics/campaignReport.js